import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, ClipboardList,
  ExternalLink, Filter, Image as ImageIcon, MessageSquare, Search,
  FileWarning, X,
} from 'lucide-react';

/**
 * /failures — consolidated drill-through for every rejected tester
 * response. Powered by GET /api/analytics/failures; each row expands
 * client-side to show the full evidence payload that's already on the
 * record (no second request).
 */

const WINDOW_OPTIONS = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
  { label: 'All time', value: 3650 },
];

const relTime = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m}mo ago`;
  return `${Math.floor(m / 12)}y ago`;
};

const EvidencePanel = ({ evidence }) => {
  const ev = evidence || {};
  const hasComments = Boolean((ev.comments || '').trim());
  const hasSteps = Array.isArray(ev.test_steps) && ev.test_steps.length > 0;
  const hasImages = Array.isArray(ev.images) && ev.images.length > 0;
  if (!hasComments && !hasSteps && !hasImages) {
    return <p className="text-white/60 text-xs italic">No evidence captured.</p>;
  }
  return (
    <div className="space-y-3 text-sm">
      {hasComments && (
        <div>
          <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Comments
          </p>
          <div className="whitespace-pre-wrap text-white/90 bg-[rgba(229,239,250,0.08)] rounded p-2 text-xs">
            {ev.comments}
          </div>
        </div>
      )}
      {hasSteps && (
        <div>
          <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
            <ClipboardList className="w-3.5 h-3.5" /> Test steps ({ev.test_steps.length})
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-white/60">
                  <th className="text-left font-medium px-1.5 py-1 w-6">#</th>
                  <th className="text-left font-medium px-1.5 py-1">Action</th>
                  <th className="text-left font-medium px-1.5 py-1">Expected</th>
                  <th className="text-left font-medium px-1.5 py-1">Actual</th>
                  <th className="text-left font-medium px-1.5 py-1 w-20">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {ev.test_steps.map((s, i) => (
                  <tr key={i} className="border-t border-[rgba(229,239,250,0.08)] align-top">
                    <td className="px-1.5 py-1 text-white/60">{i + 1}</td>
                    <td className="px-1.5 py-1 text-white/90">{s.action || '—'}</td>
                    <td className="px-1.5 py-1 text-white/90">{s.expected || '—'}</td>
                    <td className="px-1.5 py-1 text-white/90">{s.actual || '—'}</td>
                    <td className="px-1.5 py-1">
                      {s.outcome ? (
                        <span
                          className={`capitalize ${
                            s.outcome === 'pass'
                              ? 'text-green-400'
                              : s.outcome === 'fail'
                              ? 'text-red-400'
                              : 'text-amber-400'
                          }`}
                        >
                          {s.outcome}
                        </span>
                      ) : (
                        <span className="text-white/40">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {hasImages && (
        <div>
          <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> Screenshots ({ev.images.length})
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {ev.images.map((img, i) => (
              <a
                key={i}
                href={img.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block border border-[rgba(229,239,250,0.15)] rounded overflow-hidden bg-[rgba(229,239,250,0.05)] p-1"
                title={img.blob_name}
              >
                <img
                  src={img.url}
                  alt={`screenshot ${i + 1}`}
                  loading="lazy"
                  className="w-full h-20 object-cover rounded-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML =
                      '<div class="w-full h-20 flex items-center justify-center text-[10px] text-white/50">Image (auth required)</div>';
                  }}
                />
                <div className="text-[10px] text-white/50 mt-1 truncate">
                  {(img.size_bytes || 0) >= 1024
                    ? `${Math.round((img.size_bytes || 0) / 1024)} KB`
                    : `${img.size_bytes || 0} B`}
                </div>
              </a>
            ))}
          </div>
          <p className="text-[10px] text-white/40 mt-1">
            Links require your Azure Blob Storage access.
          </p>
        </div>
      )}
    </div>
  );
};

const FailureRow = ({ row, idx, expanded, onToggle }) => {
  return (
    <>
      <tr
        className={`border-t border-[rgba(229,239,250,0.08)] cursor-pointer hover:bg-[rgba(229,239,250,0.06)] ${
          expanded ? 'bg-[rgba(229,239,250,0.06)]' : ''
        }`}
        onClick={onToggle}
        data-testid={`failure-row-${idx}`}
      >
        <td className="px-2 py-2 w-6">
          {expanded ? <ChevronDown className="w-4 h-4 text-white/60" /> : <ChevronRight className="w-4 h-4 text-white/60" />}
        </td>
        <td className="px-2 py-2">
          <div className="text-white font-medium text-sm">{row.app_name}</div>
          <div className="text-white/50 text-xs">{row.publisher}{row.version ? ` · v${row.version}` : ''}</div>
        </td>
        <td className="px-2 py-2 text-white/90 text-sm whitespace-nowrap">{row.tester_email}</td>
        <td className="px-2 py-2 text-white/80 text-sm whitespace-nowrap" title={new Date(row.responded_at).toLocaleString()}>
          {relTime(row.responded_at)}
        </td>
        <td className="px-2 py-2 text-white/80 text-sm max-w-md">
          <div className="truncate" title={row.first_line}>
            {row.first_line || <span className="text-white/40 italic">no comments</span>}
          </div>
          {row.total_step_count > 0 && (
            <div className="text-[11px] text-white/50 mt-0.5">
              {row.failed_step_count}/{row.total_step_count} step{row.total_step_count === 1 ? '' : 's'} failed
            </div>
          )}
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span className="flex items-center gap-1 text-white/70">
            {row.has_comments && <MessageSquare className="w-3.5 h-3.5" title="Has comments" />}
            {row.has_steps && <ClipboardList className="w-3.5 h-3.5" title="Has test steps" />}
            {row.has_images && <ImageIcon className="w-3.5 h-3.5" title="Has screenshots" />}
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          {row.resolved ? (
            <Badge className="bg-green-600/20 text-green-400 text-[10px]">Resolved</Badge>
          ) : (
            <Badge className="bg-rose-600/20 text-rose-400 text-[10px]">Open</Badge>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[rgba(229,239,250,0.04)]" data-testid={`failure-row-expanded-${idx}`}>
          <td colSpan={7} className="px-4 py-4">
            <div className="flex items-start justify-between mb-3">
              <div className="text-xs text-white/60">
                Rejected {new Date(row.responded_at).toLocaleString()} by {row.tester_email}
              </div>
              {row.package_id && (
                <Link
                  to={`/apps/${encodeURIComponent(row.package_id)}`}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  data-testid={`failure-open-package-${idx}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Open package <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
            <EvidencePanel evidence={row.evidence} />
          </td>
        </tr>
      )}
    </>
  );
};

const FailuresPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessToken, demoMode } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  const filter = searchParams.get('filter') || '';
  const publisher = searchParams.get('publisher') || '';
  const testerFilter = searchParams.get('tester') || '';
  const packageIdFilter = searchParams.get('package_id') || '';
  const weekFilter = searchParams.get('week') || '';
  const includeResolved = searchParams.get('include_resolved') === 'true';
  const windowDays = parseInt(searchParams.get('window_days') || '90', 10);

  const title = useMemo(() => {
    if (filter === 'open') return 'Open rejections';
    if (filter === 'this-month') return 'Rejections this month';
    if (filter === 'window-30d') return 'Rejections last 30 days';
    if (publisher) return `Rejections from ${publisher}`;
    if (packageIdFilter) return 'Package rejections';
    if (weekFilter) return `Rejections for week of ${weekFilter}`;
    return 'Failures';
  }, [filter, publisher, packageIdFilter, weekFilter]);

  // Map the canonical `filter` into concrete query params.
  const effectiveWindow = useMemo(() => {
    if (filter === 'this-month') {
      const now = new Date();
      const days = now.getDate();
      return Math.max(days, 1);
    }
    if (filter === 'window-30d') return 30;
    return windowDays;
  }, [filter, windowDays]);

  const fetchFailures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = demoMode
        ? {}
        : await getAccessToken()
            .then((t) => (t ? { Authorization: `Bearer ${t}` } : {}))
            .catch(() => ({}));
      const params = {
        window_days: effectiveWindow,
        include_resolved: includeResolved,
        limit: 200,
      };
      if (publisher) params.publisher = publisher;
      if (testerFilter) params.tester = testerFilter;
      if (packageIdFilter) params.package_id = packageIdFilter;
      if (weekFilter) params.week = weekFilter;
      const res = await axios.get(`${API}/analytics/failures`, { headers, params });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load failures');
    } finally {
      setLoading(false);
    }
  }, [demoMode, getAccessToken, effectiveWindow, includeResolved, publisher, testerFilter, packageIdFilter, weekFilter]);

  useEffect(() => {
    fetchFailures();
  }, [fetchFailures]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === '' || v === null || v === undefined) next.delete(k);
    else next.set(k, String(v));
    setSearchParams(next, { replace: true });
  };
  const clearFilter = (k) => setParam(k, '');

  const rows = data?.rows || [];
  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.app_name || '').toLowerCase().includes(q) ||
        (r.publisher || '').toLowerCase().includes(q) ||
        (r.tester_email || '').toLowerCase().includes(q) ||
        (r.first_line || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const activeChips = [];
  if (publisher) activeChips.push({ k: 'publisher', label: `Publisher: ${publisher}` });
  if (testerFilter) activeChips.push({ k: 'tester', label: `Tester: ${testerFilter}` });
  if (packageIdFilter) activeChips.push({ k: 'package_id', label: `Package: ${packageIdFilter.slice(0, 10)}` });
  if (weekFilter) activeChips.push({ k: 'week', label: `Week of ${weekFilter}` });
  if (filter) activeChips.push({ k: 'filter', label: filter });

  return (
    <div className="p-6 md:p-8" data-testid="failures-page">
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-white/80 hover:text-white"
          data-testid="failures-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <FileWarning className="w-6 h-6 text-rose-400" />
          <h1 className="text-2xl font-semibold text-white">{title}</h1>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] mb-4">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-white/70 text-xs uppercase tracking-wide">
              <Filter className="w-3.5 h-3.5" /> Filters
            </div>

            <label className="flex items-center gap-2 text-xs text-white/80">
              Window
              <select
                className="bg-[rgba(229,239,250,0.1)] text-white text-xs border border-[rgba(229,239,250,0.2)] rounded px-2 py-1"
                value={filter ? filter : String(effectiveWindow)}
                onChange={(e) => {
                  // Switching window clears the preset filter shortcut.
                  setParam('filter', '');
                  setParam('window_days', e.target.value);
                }}
                data-testid="failures-window-select"
              >
                {WINDOW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs text-white/80">
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(e) => setParam('include_resolved', e.target.checked ? 'true' : '')}
                data-testid="failures-include-resolved"
              />
              Include resolved
            </label>

            {(data?.facets?.publishers || []).length > 0 && (
              <label className="flex items-center gap-2 text-xs text-white/80">
                Publisher
                <select
                  className="bg-[rgba(229,239,250,0.1)] text-white text-xs border border-[rgba(229,239,250,0.2)] rounded px-2 py-1"
                  value={publisher}
                  onChange={(e) => setParam('publisher', e.target.value)}
                  data-testid="failures-publisher-select"
                >
                  <option value="">All</option>
                  {data.facets.publishers.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </label>
            )}

            {(data?.facets?.testers || []).length > 0 && (
              <label className="flex items-center gap-2 text-xs text-white/80">
                Tester
                <select
                  className="bg-[rgba(229,239,250,0.1)] text-white text-xs border border-[rgba(229,239,250,0.2)] rounded px-2 py-1"
                  value={testerFilter}
                  onChange={(e) => setParam('tester', e.target.value)}
                  data-testid="failures-tester-select"
                >
                  <option value="">All</option>
                  {data.facets.testers.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <Search className="w-4 h-4 text-white/50" />
              <Input
                placeholder="Search app / reason / tester"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[rgba(229,239,250,0.1)] border-[rgba(229,239,250,0.2)] text-white placeholder:text-white/40 h-8 text-xs w-64"
                data-testid="failures-search-input"
              />
            </div>
          </div>

          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {activeChips.map((c) => (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => clearFilter(c.k)}
                  className="inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-300 px-2 py-0.5 rounded text-[11px] hover:bg-cyan-500/25"
                  data-testid={`failures-chip-${c.k}`}
                >
                  {c.label}
                  <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            {loading
              ? 'Loading…'
              : `${filteredRows.length} ${filteredRows.length === 1 ? 'rejection' : 'rejections'}`}
            {data?.total_matched > filteredRows.length && (
              <span className="text-white/50 text-xs">
                (of {data.total_matched} matched{data.returned < data.total_matched ? `, showing first ${data.returned}` : ''})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="text-rose-400 text-sm">{error}</p>}
          {!error && !loading && filteredRows.length === 0 && (
            <div className="text-center py-12 text-white/60">
              <FileWarning className="w-10 h-10 text-white/30 mx-auto mb-3" />
              <p>No failures match the current filters.</p>
              <p className="text-xs text-white/40 mt-1">
                {includeResolved ? 'Try widening the time window.' : 'Enable "Include resolved" to see historical rejections that were later re-approved.'}
              </p>
            </div>
          )}
          {!error && filteredRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/70 text-xs uppercase tracking-wider">
                    <th className="px-2 py-2 w-6"></th>
                    <th className="px-2 py-2 text-left font-medium">App</th>
                    <th className="px-2 py-2 text-left font-medium">Tester</th>
                    <th className="px-2 py-2 text-left font-medium">Rejected</th>
                    <th className="px-2 py-2 text-left font-medium">Reason</th>
                    <th className="px-2 py-2 text-left font-medium">Evidence</th>
                    <th className="px-2 py-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, i) => (
                    <FailureRow
                      key={`${row.token}-${i}`}
                      row={row}
                      idx={i}
                      expanded={!!expanded[row.token]}
                      onToggle={() => setExpanded((m) => ({ ...m, [row.token]: !m[row.token] }))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FailuresPage;
